import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { flow } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import classNames from 'classnames';
import { InventoryEntities } from 'client/data/models/inventory';
import { ContentSection } from 'site-modules/shared/components/content-section/content-section';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { EventToolbox } from 'client/utils/event-toolbox';
import { INVENTORY_PAGE_CUSTOM_EVENTS } from 'site-modules/shared/constants/inventory/page-events';
import { getVehicleSummaryData } from 'site-modules/shared/utils/inventory/get-vehicle-summary-data';
import { isCarZingPartner, isPrivateParty } from 'site-modules/shared/utils/dealer-details-utils';
import { VdpLeadFormWrapper } from 'site-modules/shared/components/inventory/vdp-lead-form-drawer/vdp-lead-form-wrapper';
import { VehicleSummaryItem } from 'site-modules/shared/components/inventory/vehicle-summary/vehicle-summary-item';
import { SUMMARY_CONFIG_VDP } from 'site-modules/shared/constants/inventory/vehicle-summary-config';
import { LEAD_FORM_NAMES } from 'site-modules/shared/constants/lead-form/lead-form-names';

import './vehicle-summary.scss';

const filterOutEvRangeAndChargeTime = excludeBatteryInfo => config =>
  excludeBatteryInfo
    ? config.filter(({ name }) => name !== 'electricityRange' && name !== 'batteryChargeTime')
    : config;

const filterOutFinalAssembly = isNew => config => {
  return !isNew ? config.filter(({ name }) => name !== 'finalAssembly') : config;
};

const filterOutStockNumber = isPrivatePartyListing => config => {
  return isPrivatePartyListing ? config.filter(({ name }) => name !== 'stock') : config;
};

/**
 * VehicleSummary component.
 * @param {object} vinData
 * @param {boolean} isMobile
 * @param {object} cols
 * @param {boolean} isVla
 * @param {object} vdpLeadFormParams
 * @param {boolean} exludeBatteryInfo
 * @returns {ReactElement}
 */
export function VehicleSummary({
  vinData = {},
  isMobile = false,
  cols = {
    xs: 12,
    lg: 6,
  },
  isVla = false,
  vdpLeadFormParams = {},
  excludeBatteryInfo = false,
}) {
  const { type } = vinData;
  const isUsedType = isUsed(type);
  const isPrivatePartyListing = isPrivateParty(vinData);
  const isCarZingPrequalify = isUsedType && isCarZingPartner(vinData?.dealerInfo?.partnerCode);

  const summaryData = flow([
    getVehicleSummaryData,
    filterOutEvRangeAndChargeTime(excludeBatteryInfo),
    filterOutFinalAssembly(!isUsedType),
    filterOutStockNumber(isPrivatePartyListing),
  ])(vinData);

  const firstPart = summaryData.slice(0, Math.round(summaryData.length / 2));
  const secondPart = summaryData.slice(firstPart.length);
  const isSummaryEmpty = !summaryData.some(({ name }) => !['vin', 'stock', 'finalAssembly'].includes(name));
  const isVehicleSummaryEmpty = isSummaryEmpty && !isUsedType;

  const adsDisabledConfig = {
    thankYouConfig: {
      adsConfig: { adComponent: null },
    },
  };

  function openLeadForm() {
    EventToolbox.fireCustomEvent(INVENTORY_PAGE_CUSTOM_EVENTS.OPEN_LEAD_FORM);
  }

  return (
    <ContentSection
      className="vehicle-summary w-100"
      dataTrackingParent={`edm-entry-vehicle-summary${isCarZingPrequalify ? '-carzing_prequal' : ''}`}
      titleClassName="section-title heading-4 mb-1_5"
      line={false}
      headerTag="h3"
      sectionTitle="Highlights"
    >
      {isVehicleSummaryEmpty && (
        <div className="empty-vehicle-summary">
          <div>This vehicle was recently added to the lot! Contact the dealer for details and availability.</div>
          {!isVla && (
            <Fragment>
              <div className="my-1_5">
                <Button
                  color="outline-blue-50"
                  className="size-16"
                  style={{
                    paddingLeft: '40px',
                    paddingRight: '40px',
                  }}
                  onClick={openLeadForm}
                  data-tracking-id="view_pricing_details"
                  data-tracking-value="contact_dealer"
                >
                  Contact Dealer
                </Button>
              </div>
              <VdpLeadFormWrapper
                vinData={vinData}
                {...vdpLeadFormParams}
                formName={LEAD_FORM_NAMES.SIMPLE_INVENTORY_LEAD_FORM}
                customConfig={adsDisabledConfig}
              />
            </Fragment>
          )}
        </div>
      )}
      {!!summaryData.length && (
        <Row className="m-0 max-width text-capitalize px-0">
          {(isMobile ? [summaryData] : [firstPart, secondPart]).map((values, ind) => (
            <Col
              tag="ul"
              {...cols}
              className={classNames('px-0 mb-0', {
                'ps-0 pe-lg-1 pb-1 pb-lg-0 border-bottom-gray': !isMobile && ind === 0,
                'pe-0 ps-lg-1 pb-0 pt-1 pt-lg-0': !isMobile && ind === 1,
              })}
              // eslint-disable-next-line react/no-array-index-key
              key={ind}
            >
              {values.map(({ name, value }) => (
                <VehicleSummaryItem
                  key={name}
                  vinData={vinData}
                  name={name}
                  value={value}
                  summaryDisplayConfig={SUMMARY_CONFIG_VDP}
                />
              ))}
            </Col>
          ))}
        </Row>
      )}
    </ContentSection>
  );
}

VehicleSummary.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  isMobile: PropTypes.bool,
  cols: PropTypes.shape({}),
  isVla: PropTypes.bool,
  vdpLeadFormParams: PropTypes.shape({}),
  excludeBatteryInfo: PropTypes.bool,
};
